import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ChatBubbleLeftRightIcon,
  TrashIcon,
  CheckIcon,
  NewspaperIcon,
  UserIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import {
  fetchComments,
  deleteComment,
  bulkDeleteComments,
  toggleCommentSelection,
  setSelectedComments,
  clearSelectedComments
} from '../../store/slices/commentSlice';
import { showSuccess, showError } from '../../store/slices/notificationSlice';
import { Card, Button, Loading, Pagination, SearchInput, ConfirmDialog } from '../../components/Common';

const CommentList = () => {
  const dispatch = useDispatch();
  const { comments, loading, pagination, selectedComments } = useSelector((state) => state.comment);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  const loadComments = useCallback(() => {
    dispatch(fetchComments({ 
      page: currentPage, 
      size: 20, 
      search: searchTerm 
    }));
  }, [dispatch, currentPage, searchTerm]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleDeleteClick = (comment) => {
    setCommentToDelete(comment);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteComment(commentToDelete.id)).unwrap();
      dispatch(showSuccess('Comment deleted successfully'));
      setDeleteDialogOpen(false);
      setCommentToDelete(null);
    } catch (error) {
      dispatch(showError(error || 'Failed to delete comment'));
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedComments.length === 0) {
      dispatch(showError('Please select comments to delete'));
      return;
    }
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      await dispatch(bulkDeleteComments(selectedComments)).unwrap();
      dispatch(showSuccess(`${selectedComments.length} comments deleted successfully`));
      setBulkDeleteDialogOpen(false);
    } catch (error) {
      dispatch(showError(error || 'Failed to delete comments'));
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      dispatch(setSelectedComments(comments.map(c => c.id)));
    } else {
      dispatch(clearSelectedComments());
    }
  };

  const handleSelectComment = (id) => {
    dispatch(toggleCommentSelection(id));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading && comments.length === 0) {
    return <Loading fullScreen text="Loading comments..." />;
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="w-8 h-8 text-blue-600" />
            Comments Management
          </h1>
          <p className="text-slate-500 mt-1">
            Manage and moderate article comments
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Comments</p>
              <p className="text-2xl font-bold text-blue-900">{pagination.total}</p>
            </div>
            <ChatBubbleLeftRightIcon className="w-12 h-12 text-blue-400" />
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">This Page</p>
              <p className="text-2xl font-bold text-green-900">{comments.length}</p>
            </div>
            <CheckIcon className="w-12 h-12 text-green-400" />
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Selected</p>
              <p className="text-2xl font-bold text-purple-900">{selectedComments.length}</p>
            </div>
            <CheckIcon className="w-12 h-12 text-purple-400" />
          </div>
        </Card>
      </div>

      {/* Actions Bar */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="w-full md:w-96">
            <SearchInput
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search comments by content or author..."
            />
          </div>
          
          <div className="flex gap-2">
            {selectedComments.length > 0 && (
              <Button
                variant="danger"
                onClick={handleBulkDeleteClick}
                className="flex items-center gap-2"
              >
                <TrashIcon className="w-4 h-4" />
                Delete Selected ({selectedComments.length})
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={loadComments}
              disabled={loading}
            >
              Refresh
            </Button>
          </div>
        </div>
      </Card>

      {/* Comments List */}
      <Card>
        {comments.length === 0 ? (
          <div className="text-center py-12">
            <ChatBubbleLeftRightIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No comments found</h3>
            <p className="text-gray-500">
              {searchTerm ? 'Try adjusting your search terms' : 'Comments will appear here once users start commenting on articles'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Select All */}
            <div className="flex items-center gap-2 pb-3 border-b">
              <input
                type="checkbox"
                checked={selectedComments.length === comments.length && comments.length > 0}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">
                Select All ({comments.length})
              </span>
            </div>

            {/* Comment Items */}
            {comments.map((comment) => (
              <div
                key={comment.id}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedComments.includes(comment.id)
                    ? 'border-blue-300 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedComments.includes(comment.id)}
                    onChange={() => handleSelectComment(comment.id)}
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />

                  {/* Comment Content */}
                  <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4 text-gray-400" />
                          <span className="font-semibold text-gray-900">
                            {comment.author_name || `${comment.first_name || ''} ${comment.last_name || ''}`.trim() || 'Anonymous'}
                          </span>
                        </div>
                        <span className="text-gray-400">•</span>
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <CalendarIcon className="w-4 h-4" />
                          {formatDate(comment.created_at)}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteClick(comment)}
                          className="flex items-center gap-1"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Delete
                        </Button>
                      </div>
                    </div>

                    {/* Article Info */}
                    {comment.article_title && (
                      <div className="flex items-center gap-2 mb-2 text-sm text-gray-600">
                        <NewspaperIcon className="w-4 h-4" />
                        <span>On article:</span>
                        <span className="font-medium text-blue-600">{comment.article_title}</span>
                      </div>
                    )}

                    {/* Comment Text */}
                    <div className="bg-gray-50 rounded-lg p-3 mb-2">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {truncateText(comment.content, 300)}
                      </p>
                    </div>

                    {/* Footer Info */}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>ID: {comment.id}</span>
                      {comment.article_id && <span>Article ID: {comment.article_id}</span>}
                      {comment.parent_comment_id && (
                        <span className="text-blue-600">Reply to comment #{comment.parent_comment_id}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setCommentToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Comment"
        message={
          <div>
            <p className="mb-2">Are you sure you want to delete this comment?</p>
            {commentToDelete && (
              <div className="bg-gray-50 p-3 rounded-lg mt-3">
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Author:</strong> {commentToDelete.author_name || 'Anonymous'}
                </p>
                <p className="text-sm text-gray-700">
                  "{truncateText(commentToDelete.content, 100)}"
                </p>
              </div>
            )}
            <p className="text-red-600 text-sm mt-3">This action cannot be undone.</p>
          </div>
        }
        confirmText="Delete"
        confirmVariant="danger"
      />

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDeleteConfirm}
        title="Delete Multiple Comments"
        message={
          <div>
            <p className="mb-2">
              Are you sure you want to delete <strong>{selectedComments.length}</strong> selected comment(s)?
            </p>
            <p className="text-red-600 text-sm mt-3">This action cannot be undone.</p>
          </div>
        }
        confirmText={`Delete ${selectedComments.length} Comment(s)`}
        confirmVariant="danger"
      />
    </div>
  );
};

export default CommentList;
